import React from 'react';

// In-app deep-dive for the reference color-match ("Auto-match" / Color
// Assistant) pipeline: RED RAW frame + a graded reference still -> a 3D LUT +
// grade that reproduces the look, baked into the same engine as preview/export.
// Kept in sync with backend/colortransfer.py, backend/_tuckertransfer.mjs and
// utils/grade.js. Full write-up: docs/color-reference-match.md.

const STEPS = [
  [
    '1 · Decode RED correctly (scene-referred)',
    'The RED proxy is flat Log3G10 / Wide-Gamut — NOT sRGB. We develop it scene-referred first (log3g10 → scene-linear → RWG→Rec.709 → exposure → highlight roll-off → filmic → encode) with auto-exposure, so the baseline has real contrast and highlight detail instead of a clipped, grey RED flat. This single fix mattered most.',
  ],
  [
    '2 · Sliced optimal-transport distribution transfer',
    'Pitié-style iterative distribution transfer (IDT): repeatedly project both images onto a random orthonormal basis and match the per-axis CDF source→reference. This matches the full joint colour+tone distribution, not just mean/std, reproducing the cinematic spread (teal mids, warm highlights, contrast). Fit in both OKLab and CIELab; keep whichever scores lower. Transfer is kept LIGHT so the water\'s natural variation + micro-contrast survive.',
  ],
  [
    '3 · Realism-first parametric look + adaptive barrel key',
    'A per-pixel look (so it bakes into the LUT) that avoids the "painted-on" teal: a gentle contrast S-curve, then SELECTIVE, MULTIPLICATIVE teal — where a pixel is already cyan/blue in the mids, scale its chroma up (multiplicative scaling preserves each pixel\'s own hue/chroma so gradients stay real), a tiny cyan-green hue nudge on keyed water only, small additive warmth in the highlights, and a gentle global vibrance faded out in highlights. The water key is calibrated per-frame by a k-means "barrel-probability" cluster in Lab, so the teal lands exactly where this image\'s barrel actually sits — sharper regional separation while staying pointwise / LUT-bakeable.',
  ],
  [
    '4 · Bake a 45³ 3D LUT',
    'Transfer + look are evaluated on an identity 45³ grid and written to a .cube, imported as the grade\'s LUT. It runs AFTER the base de-log grade — in both the WebGL preview and the ffmpeg bake — so preview == export holds.',
  ],
  [
    '5 · Halo-free detail recovery (post-LUT)',
    'Micro-contrast/texture is spatial so it can\'t bake into the (pointwise) LUT — it runs after it in the app. A large-radius unsharp RINGS (halos) on wave/spray edges, so the delivered grade keeps the 7×7 clarity low and leans on the small-radius 3×3 texture (+ moderate 5×5 sharpen, a touch of grain). For scoring + the proof a true edge-preserving bilateral high-pass (image − bilateral) crisps texture with no halo, so the optimizer is rewarded for real detail.',
  ],
  [
    '6 · An honest, spatially-aware metric',
    'The old rank-paired distribution ΔE was spatially blind and rewarded flat results. Since it\'s the same scene, we ECC-align the reference to the result, then score per-pixel CIEDE2000 (global + barrel/mid/sky regions), MS-SSIM (structure), and gradient-magnitude difference (texture) — combined as hybrid = ΔE + 25·(1−SSIM) + 30·grad, which is what the search optimizes.',
  ],
];

const FILES = [
  ['backend/colortransfer.py', 'numpy engine — IDT, look stack, LUT bake, aligned hybrid metric.'],
  ['backend/_tuckertransfer.mjs', 'Orchestrator — develop neutral (log3g10) → run transfer → save grade.lut on the asset.'],
  ['backend/_colormatch.mjs', 'Earlier headless guarded hill-climb / band-guided matcher.'],
  ['frontend/src/utils/colorMatch.js', 'In-browser match metric + targeted moves.'],
  ['frontend/src/components/ColorAssistant.js', 'Color Assistant UI that runs the match.'],
  ['frontend/src/utils/grade.js', 'applyInputTransform (RED de-log), compileLut, lutFromCubeText, autoExposureStops.'],
];

export default function ReferenceMatchDoc() {
  return (
    <div className="cs-cmp">
      <p className="cs-section-sub">
        <b>Reference match</b> takes a flat RED clip frame and a professionally graded
        reference still and produces a 3D LUT + grade that reproduces the look — baked into
        the same engine that powers preview and export. It&apos;s classical colour science (no ML
        training), tuned to stay <i>real</i>: natural colour variation, water texture and
        highlight roll-off rather than a flat, painted-on teal. Full write-up in{' '}
        <code>docs/color-reference-match.md</code>.
      </p>

      <div className="cs-cmp-block">
        <div className="cs-cmp-tabletitle">
          <h3>Pipeline</h3>
          <span className="cs-cmp-vs">RED Log3G10 frame + graded reference → grade.lut</span>
        </div>
        <div className="cs-cmp-table">
          {STEPS.map(([k, v]) => (
            <div key={k} className="cs-cmp-row">
              <span className="cs-cmp-badge is-par">Step</span>
              <div className="cs-cmp-cell">
                <span className="cs-cmp-feat">{k}</span>
                <span className="cs-cmp-note">{v}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="cs-cmp-block">
        <div className="cs-cmp-tabletitle"><h3>Where it lives</h3></div>
        <div className="cs-cmp-table">
          {FILES.map(([k, v]) => (
            <div key={k} className="cs-cmp-row">
              <span className="cs-cmp-badge is-win">File</span>
              <div className="cs-cmp-cell">
                <span className="cs-cmp-feat">{k}</span>
                <span className="cs-cmp-note">{v}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="cs-cmp-block">
        <div className="cs-cmp-tabletitle"><h3>Roadmap</h3></div>
        <div className="cs-cmp-table">
          {[
            ['Edge-aware clarity in-app', 'A guided-filter local contrast baked into the app\'s GLSL + ffmpeg with full preview==export parity, so the DELIVERED grade — not just the proof — is halo-free by construction.'],
            ['Tracker-linked / per-region grade', 'Drive a local grade from the subject path or the barrel-probability map for true spatial separation beyond a pointwise LUT.'],
          ].map(([k, v]) => (
            <div key={k} className="cs-cmp-row">
              <span className="cs-cmp-badge is-missing">Planned</span>
              <div className="cs-cmp-cell">
                <span className="cs-cmp-feat">{k}</span>
                <span className="cs-cmp-note">{v}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
